import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useState } from "react";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { actSubjects } from "../../actprep-ui/constants/program";
import {
  capitalize,
  formatConstantOptions,
} from "../../actprep-ui/utils/formatting";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import RadioGroup from "../../actprep-ui/components/RadioGroup";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import {
  timingOptions,
  testingTypes,
  mockActEventTypes,
} from "../../actprep-ui/constants/testing";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers-pro";

export default function MockACTEventForm({ row, onCreate, onEdit }) {
  const [title, setTitle] = useState(row?.title || "");
  const [eventType, setEventType] = useState(row?.event_type || "online");
  const [eventDt, setEventDt] = useState(
    row?.event_dt ? dayjs(row.event_dt) : null
  );
  const [timingOption, setTimingOption] = useState(
    row?.timing_option || timingOptions.DEFAULT
  );
  const [testingType, setTestingType] = useState(
    row?.testing_type || testingTypes.FULL_TEST
  );
  const [test, setTest] = useState(
    row?.test
      ? {
          label: row.test.visible_name,
          uuid: row.test.uuid,
        }
      : null
  );
  const [school, setSchool] = useState(
    row?.school
      ? {
          label: row.school.name,
          uuid: row.school.uuid,
        }
      : null
  );
  const [subject, setSubject] = useState(row?.subject || "");
  const [isChanged, setIsChanged] = useState(false);
  const isCreateMode = !row?.uuid;

  const handleReset = () => {
    // TODO: Fix this - setFormData(getCategoryFormData(initialCategory.current));
    setIsChanged(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = {
      title: title,
      test_id: test?.uuid,
      school_id: school?.uuid,
      event_type: eventType,
      event_dt: eventDt.toDate().toISOString(),
      subject: subject,
      testing_type: testingType,
      timing_option: timingOption,
    };
    if (isCreateMode) {
      const response = await api.post(apiUrls.admin.mockActEvents(), formData);
      showSuccessSnackbar("Mock ACT Event created successfully!");
      onCreate(response.data.data);
    } else {
      const response = await api.put(
        apiUrls.admin.mockActEvents(row.uuid),
        formData
      );
      showSuccessSnackbar("Mock ACT Event updated successfully!");
      onEdit(response.data.data);
    }
  };

  const timingOptionRadioOptions = formatConstantOptions(timingOptions);
  const testingTypeRadioOptions = formatConstantOptions(testingTypes);
  const mockActEvenTypeOptions = formatConstantOptions(mockActEventTypes);

  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid size={12}>
          <Typography variant="overline" fontWeight={"bold"} fontSize={"large"}>
            Event Info
          </Typography>
          <TextField
            fullWidth
            value={title}
            onChange={(event) => {
              setIsChanged(true);
              setTitle(event.target.value);
            }}
            label="Title"
            variant="outlined"
            type="text"
            required
          />
        </Grid>
        <Grid size={12}>
          <DateTimePicker
            value={eventDt}
            onChange={(newVal) => {
              setIsChanged(true);
              setEventDt(newVal);
            }}
            label="Event Date/Time"
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
              },
            }}
          />
        </Grid>
        <Grid size={12}>
          <RadioGroup
            label={"Event Type"}
            value={eventType}
            setValue={(newVal) => {
              setIsChanged(true);
              setEventType(newVal);
            }}
            options={mockActEvenTypeOptions}
          />
        </Grid>
        {eventType == "school" && (
          <Grid size={12}>
            <ModelAutoComplete
              modelName={"School"}
              label={"School"}
              value={school}
              setValue={(newVal) => {
                setIsChanged(true);
                setSchool(newVal);
              }}
              variant="outlined"
              required
            />
          </Grid>
        )}
        <Grid size={12}>
          <Typography variant="overline" fontWeight={"bold"} fontSize={"large"}>
            Test Info
          </Typography>
          <br />
          <RadioGroup
            label={"Testing Type"}
            value={testingType}
            setValue={(newVal) => {
              setIsChanged(true);
              setTestingType(newVal);
            }}
            options={testingTypeRadioOptions}
          />
        </Grid>
        <Grid size={12}>
          <RadioGroup
            label={"Timing Option"}
            value={timingOption}
            setValue={(newVal) => {
              setIsChanged(true);
              setTimingOption(newVal);
            }}
            options={timingOptionRadioOptions}
          />
        </Grid>
        <Grid size={12}>
          <ModelAutoComplete
            modelName={"Test"}
            label={"Test"}
            value={test}
            setValue={(newVal) => {
              setIsChanged(true);
              setTest(newVal);
            }}
            variant="outlined"
            required
          />
        </Grid>
        {testingType == testingTypes.SUBJECT_TEST && (
          <Grid size={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Subject</InputLabel>
              <Select
                value={subject}
                onChange={(event) => {
                  setIsChanged(true);
                  setSubject(event.target.value);
                }}
                label="Subject"
                required
              >
                {actSubjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {capitalize(subject)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid size={12}>
          <ButtonGroup fullWidth disabled={!isChanged}>
            <Button disabled={isCreateMode} onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit">{isCreateMode ? "Create" : "Save"}</Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Box>
  );
}
